import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import {
  TextField,
  Box,
  Typography,
  Button,
  Alert,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import { useContext } from "react";
import { ReturContext } from "./ReturContext";

const initialFormState = {
  id: "",
  nume: "",
  email: "",
  telefon: "",
  adresa: "",
  oras: "",
  judet: "",
  cod_postal: "",
  created_at: "",
  awb: "",
  Produse: [],
};

const CreareaCerereRetur = ({ id }) => {
  console.log("[✅ DEBUG] ID primit în CreareaCerereRetur:", id);
  const [form, setForm] = useState(() => ({ ...initialFormState }));

  const [selectedIndexes, setSelectedIndexes] = useState([]);
  const [statuses, setStatuses] = useState({});

  const fetchData = async () => {
    try {
      const response = await axios.get(
        `https://server-9p4hx.kinsta.app/api/stocmanager/cerere-restituit/get/${id}`
      );
      setForm(response.data);
    } catch (error) {
      console.error("Eroare la încărcarea cererii:", error);
    }
  };

  const fetchStatuses = async () => {
    const statusMap = {};

    for (const produs of form.Produse) {
      if (produs.issue_id) {
        try {
          console.log(
            "[🔁 JIRA STATUS] Fetch pentru issue_id:",
            produs.issue_id
          );
          const response = await axios.get(
            `https://server-9p4hx.kinsta.app/api/tasks/jira-task/${produs.issue_id}`
          );
          const currentStatus = response.data.status;
          console.log(
            "[✅ STATUS] Issue:",
            produs.issue_id,
            "→",
            currentStatus
          );
          if (currentStatus === "RESTITUIT") {
            statusMap[produs.issue_id] = "RESTITUIT";
          } else {
            statusMap[produs.issue_id] = currentStatus;
          }
        } catch (err) {
          console.error(
            `Eroare la fetch status pentru ${produs.issue_id}:`,
            err.message
          );
        }
      }
    }

    setStatuses(statusMap);
  };

  useEffect(() => {
    console.log("[🔍 ID primit în CerereRetur]:", id);
    if (id) fetchData();
  }, [id]);

  useEffect(() => {
    if (form.Produse.length > 0) fetchStatuses();
  }, [form.Produse]);

  const handleSelectProduct = (index) => {
    setSelectedIndexes((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
    console.log("[🟦 Produs selectat]:", form.Produse[index]);
  };

  // Memoize produsele selectate pentru performanță stabilă
  const produseSelectate = useMemo(
    () => form.Produse.filter((_, idx) => selectedIndexes.includes(idx)),
    [form.Produse, selectedIndexes]
  );

  const handleCreareNotaRetur = async () => {
    if (produseSelectate.length === 0) {
      alert(
        "Te rugăm să selectezi cel puțin un produs înainte de a crea nota de retur."
      );
      return;
    }

    const payload = { ...form, Produse: produseSelectate };
    console.log("[📦 Payload trimis către backend]:", payload);

    try {
      const response = await axios.post(
        "https://server-9p4hx.kinsta.app/api/stocmanager/cerere-restituit/generare-nota-retur",
        payload,
        { responseType: "blob" }
      );

      const contentDisposition = response.headers["content-disposition"] || "";
      const isZip = contentDisposition.includes(".zip");

      const blob = new Blob([response.data], {
        type: isZip ? "application/zip" : "application/pdf",
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = isZip
        ? `note_retur_${form.id}.zip`
        : `Nota_restituit_${form.id}_${new Date().toLocaleDateString("ro-RO").replace(/\./g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (error) {
      console.error("Eroare la generarea notei de retur:", error);
      alert("A apărut o eroare la generarea PDF-ului.");
    }
  };

  const handleAnuleazaProdus = async (issueId) => {
    const produsTarget = form.Produse.find((p) => p.issue_id === issueId);
    if (!produsTarget) {
      alert(`Produsul cu issue_id ${issueId} nu a fost găsit.`);
      return;
    }

    try {
      const response = await axios.post(
        "https://server-9p4hx.kinsta.app/api/stocmanager/cerere-restituit/anuleaza-produs",
        {
          cerereId: form.id,
          issue_id: issueId,
        }
      );

      if (response.data?.cerereEliminata) {
        setForm({ ...initialFormState });
        setSelectedIndexes([]);
        setStatuses({});
        alert(
          "Toată cererea a fost ștearsă deoarece nu mai există produse restante."
        );
        return;
      }

      // feedback instant pentru utilizator
      setForm((prev) => ({
        ...prev,
        Produse: prev.Produse.filter((p) => p.issue_id !== issueId),
      }));
      setSelectedIndexes((prev) =>
        prev.filter((idx) => form.Produse[idx]?.issue_id !== issueId)
      );
      setStatuses((prev) => {
        const updated = { ...prev };
        delete updated[issueId];
        return updated;
      });

      fetchData(); // sincronizare în fundal
    } catch (error) {
      console.error("Eroare la ștergerea produsului:", error);
      alert("A apărut o eroare la ștergerea produsului.");
    }
  };

  const { setReturData } = useContext(ReturContext);

  useEffect(() => {
    if (form.email || form.nume || form.telefon) {
      setReturData({
        returEmail: form.email || "",
        returNume: form.nume || "",
        returTelefon: form.telefon || "",
      });
    }
  }, [form]);

  const handleSetExpediat = async () => {
    try {
      await axios.post(
        "https://server-9p4hx.kinsta.app/api/stocmanager/cerere-restituit/set-expediat",
        {
          id: form.id,
          expediat: 1,
        }
      );
      fetchData();
      alert(
        'Statusul "expediat" a fost setat cu succes și formularul a fost actualizat.'
      );
    } catch (error) {
      console.error("Eroare la setarea statusului expediat:", error);
      alert("A apărut o eroare la setarea statusului expediat.");
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
      <Alert
        severity="info"
        sx={{
          backgroundColor: "#E3F2FD",
          color: "#0D47A1",
          fontWeight: "normal",
          mb: 2,
        }}
      >
        Verifică adresa și dacă nu o avem, te rog să o adaugi în Vendor Info cu
        tagul <strong>retur</strong>.
      </Alert>

      <Box sx={{ display: "flex", gap: 2 }}>
        <TextField label="ID" value={form.id} fullWidth />
        <TextField label="Nume" value={form.nume} fullWidth />
      </Box>

      <Box sx={{ display: "flex", gap: 2 }}>
        <TextField label="Telefon" value={form.telefon} fullWidth />
        <TextField label="Email" value={form.email} fullWidth />
      </Box>

      <TextField label="Adresă" value={form.adresa} fullWidth />

      <Box sx={{ display: "flex", gap: 2 }}>
        <TextField label="Județ" value={form.judet} fullWidth />
        <TextField label="Oraș" value={form.oras} fullWidth />
      </Box>

      <Box sx={{ display: "flex", gap: 2 }}>
        <TextField label="Cod Poștal" value={form.cod_postal} fullWidth />
        <TextField label="AWB" value={form.awb} fullWidth />
      </Box>

      <TextField
        label="Data Cerere"
        value={
          form.created_at
            ? new Date(form.created_at).toLocaleDateString("ro-RO")
            : ""
        }
        fullWidth
      />

      <Box sx={{ mt: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
          Produse
        </Typography>
        {form.Produse.map((prod, idx) => (
          <Box
            key={idx}
            sx={{
              mt: 1,
              p: 2,
              border: "2px solid",
              borderColor:
                statuses[prod.issue_id] === "RESTITUIT"
                  ? "#9ccc65"
                  : selectedIndexes.includes(idx)
                    ? "#1976d2"
                    : "#ccc",
              borderRadius: 2,
              background:
                statuses[prod.issue_id] === "RESTITUIT"
                  ? "linear-gradient(90deg, #d0f0c0 0%, #f0fff0 100%)"
                  : selectedIndexes.includes(idx)
                    ? "#E3F2FD"
                    : "inherit",
              cursor: "pointer",
              position: "relative",
            }}
            onClick={() => {
              if (statuses[prod.issue_id] !== "RESTITUIT") {
                handleSelectProduct(idx);
              }
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography>
                <strong>Summary:</strong> {prod.summary}
              </Typography>
            </Box>
            <Typography>
              <strong>Data Intrare:</strong>{" "}
              {new Date(prod.data_de_intrare).toLocaleDateString("ro-RO")}
            </Typography>
            <Typography>
              <strong>ID Contract:</strong> {prod.Id_contract}
            </Typography>
            <Button
              variant="outlined"
              onClick={(e) => {
                e.stopPropagation();
                handleAnuleazaProdus(prod.issue_id);
              }}
              sx={{
                color: "#D81B60",
                borderColor: "#D81B60",
                fontWeight: "bold",
                textTransform: "none",
                fontSize: "12px",
                position: "absolute",
                bottom: 8,
                right: 8,
                padding: "2px 8px",
                minWidth: "auto",
              }}
              endIcon={<CloseIcon sx={{ fontSize: "16px" }} />}
            >
              Anulare Restituire
            </Button>
          </Box>
        ))}
      </Box>

      <Box sx={{ mt: 4, display: "flex", flexDirection: "row", gap: 2 }}>
        <Button
          variant="contained"
          startIcon={<NoteAddIcon />}
          fullWidth
          sx={{
            height: 56,
            fontWeight: "bold",
            fontSize: "16px",
            background: "linear-gradient(90deg, #6a11cb 0%, #2575fc 100%)",
            color: "#fff",
            boxShadow: "0px 4px 12px rgba(106, 17, 203, 0.3)",
            "&:hover": {
              background: "linear-gradient(90deg, #5a0fcf 0%, #1f5eea 100%)",
            },
          }}
          onClick={handleCreareNotaRetur}
        >
          Creare Notă Restituit
        </Button>
        <Button
          variant="contained"
          startIcon={<LocalShippingIcon />}
          fullWidth
          sx={{
            height: 56,
            fontWeight: "bold",
            fontSize: "16px",
            background: "linear-gradient(90deg, #43cea2 0%, #185a9d 100%)",
            color: "#fff",
            boxShadow: "0px 4px 12px rgba(67, 206, 162, 0.3)",
            "&:hover": {
              background: "linear-gradient(90deg, #3dbb91 0%, #144e8c 100%)",
            },
          }}
          onClick={handleSetExpediat}
        >
          Expediat
        </Button>
      </Box>
    </Box>
  );
};

export default CreareaCerereRetur;
